import { upperCase as _upperCase } from 'lodash-es';
import { Person } from '../../models';

export const convertHCPNameToLabel = (firstName: string, lastName: string) => {
  return firstName && lastName ? `${lastName}, ${firstName}` : firstName || lastName || '';
};

export const convertHCPNameToFirstLastLabel = (fullName: string | null | undefined): string => {
  if (!fullName) {
    return '';
  }
  const parts = fullName.split(',');

  if (parts.length < 2) {
    return fullName.trim();
  }

  const lastName = parts[0].trim();
  const firstName = parts[1].trim();

  return `${firstName} ${lastName}`;
};

const hasAsianCharactersRegExp = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f\u3131-\uD79D]/g;
const hasOnlyAsianCharactersRegExp =
  /^[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f\u3131-\uD79D]*$/g;

export const getInitials = (person: Person) => {
  if (!person) return '';
  const fullNameAsianStyle = `${person.lastName ?? ''}${person.firstName ?? ''}`;
  const containsAsianSymbols = fullNameAsianStyle.match(hasAsianCharactersRegExp);
  const containsOnlyAsianSymbols = fullNameAsianStyle.match(hasOnlyAsianCharactersRegExp);

  if (containsOnlyAsianSymbols) {
    if (fullNameAsianStyle.length <= 4) return fullNameAsianStyle;
    return fullNameAsianStyle[0];
  } else if (containsAsianSymbols) {
    return fullNameAsianStyle[0];
  }
  return (
    _upperCase(person.firstName ? person.firstName[0] : '') + _upperCase(person.lastName ? person.lastName[0] : '')
  );
};

export const getInitialHcpEngagement = (person: Person): string => {
  if (!person || !person.fullName) {
    return '?';
  }

  let fullName = person.fullName.trim();
  fullName = fullName.replace(/'S\s+/g, '');

  if (fullName.includes(',')) {
    const [lastNamePart, firstNamePart] = fullName.split(',').map((part: string) => part.trim());

    if (firstNamePart && lastNamePart) {
      const firstInitial = firstNamePart.charAt(0).toUpperCase();
      const lastInitial = lastNamePart.charAt(0).toUpperCase();
      return `${firstInitial}${lastInitial}`;
    }
  }

  const nameParts = fullName.split(/\s+/).filter((part: string) => part.length > 0);

  if (nameParts.length >= 2) {
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  } else if (nameParts.length === 1) {
    const singleName = nameParts[0];
    return singleName.length > 1
      ? `${singleName.charAt(0).toUpperCase()}${singleName.charAt(1).toUpperCase()}`
      : singleName.charAt(0).toUpperCase().repeat(2);
  }
  return fullName.substring(0, 2).toUpperCase();
};

export const joinStringValues = <T extends Record<string, unknown>>(obj: T, filter: (keyof T)[]) =>
  obj
    ? Object.entries(obj)
        .filter(([k, v]) => typeof v === 'string' && filter.includes(k) && !!v)
        .map(([, value]) => value)
        .join(', ')
    : '';

export const buildDownloadFileNamePrefix = (base: string, name: string): string => {
  const sanitized = name
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return sanitized ? `${base}_${sanitized}` : base;
};
